import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect, useSelector } from "react-redux";
import { injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { useLocation } from "react-router";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { selectTranslations } from "../../../config/i18n/slice";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { ErrorIcon, EyeIcon } from "../../../../Assets/Icons/svg";
import { login, resetPassword } from "../../../config/api/authApis";
import {PASSWORD_REGEX} from "../../../config/common/commonFunctions";

const initialValues = {
  password: "",
  passwordConfirmation: "",
};

function ResetPassword(props) {
  const { intl } = props;
  const t = useSelector(selectTranslations);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const lang = useSelector((state) => state.i18n.lang);

  const curentUrl = useLocation();
  const queryParams = new URLSearchParams(curentUrl.search);
  const codeData = queryParams.get("code");
  const ResetPasswordSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, t.common.min_8_symbol ?? "Minimum 8 characters")
        .max(50, t.common.max_50_symbol ?? "Maximum 50 characters")
        .matches(
            PASSWORD_REGEX,
            t.common.password_validation ??
            "Must include upper, lower, number, and special character"
        )
        .required(t.common.required_field),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref("password"), null], t.common.password_should_match) // Ensure it matches the "password" field
      .required(t.common.required_field),
  });

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return `${lang == "ar" ? "is-invalid is-invalid-rtl" : "is-invalid"}`;
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return `${lang == "ar" ? "is-valid is-valid-rtl" : "is-valid"}`;
    }

    return "";
  };

  useEffect(() => {
    if (codeData) {
      setCode(codeData);
    } else {
      history.push("/auth/login");
    }
  }, [lang]);

  useEffect(() => {
    if (formik && formik.touched) {
      formik.setFieldTouched("password", false);
      formik.setFieldTouched("passwordConfirmation", false);
    }
  }, [lang]);

  const formik = useFormik({
    initialValues,
    validationSchema: ResetPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      enableLoading();
      setTimeout(() => {
        resetPassword(code, values.password, values.passwordConfirmation)
          .then(({ data: { jwt, user } }) => {
            disableLoading();
            toast.success(t.auth.reset_password_form.reset_success_password);
            login(user.email, values.password)
              .then(({ data: { jwt, user } }) => {
                disableLoading();
                if (
                  user.role.name === "admin" ||
                  user.role.name === "super_admin"
                ) {
                  props.login(jwt);
                } else {
                  history.push("/auth/login");
                }
              })
              .catch((err) => {
                history.push("/auth/login");
              });
          })
          .catch(() => {
            setStatus(t.auth.create_password_form.create_link_expired);
          })
          .finally(() => {
            disableLoading();
            setSubmitting(false);
          });
      }, 1000);
    },
  });

  return (
    <div
      className="login-form d-flex flex-column  login-signin"
      id="kt_login_signin_form"
    >
      <div className="text-center image-container mt-8">
        <img
          alt="Logo"
          className="logo-img"
          src={toAbsoluteUrl("/media/bg/logo.svg")}
        />
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework d-block"
      >
        <div>
          <h3 className="font-size-h1">{t.auth.reset_password_form.title}</h3>
        </div>
        {formik.status ? (
          <div className="mt-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        ) : null}

        <div
          className={`form-group fv-plugins-icon-container ${
            formik.status ? "mt-8" : "mt-17"
          }`}
        >
          <div className="password-container">
            <label
              className={`form-label mb-0 ${
                lang == "ar" ? "direction-rtl" : ""
              }`}
            >
              {t.auth.reset_password_form.new_password}
            </label>
            <input
              placeholder={t.auth.reset_password_form.enter_new_password}
              type={showNewPassword ? "text" : "password"}
              className={`form-control form-control-solid ${getInputClasses(
                "password"
              )} email-password-ltr ${
                formik.touched.password && formik.errors.password
                  ? "input-error"
                  : ""
              }`}
              name="password"
              {...formik.getFieldProps("password")}
            />
            <span
              className={`${
                lang == "ar" ? "password-toggle-rtl" : "password-toggle"
              }`}
            >
              {showNewPassword ? (
                <i
                  id="eyeIcon"
                  className="fa fa-eye"
                  aria-hidden="true"
                  onClick={() => setShowNewPassword(false)}
                ></i>
              ) : (
                <span onClick={() => setShowNewPassword(true)}>
                  <EyeIcon />
                </span>
              )}
            </span>
          </div>
          {formik.touched.password && formik.errors.password ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block d-flex mt-2">
                {" "}
                <ErrorIcon /> <p className="ml-1"> {formik.errors.password}</p>
              </div>
            </div>
          ) : null}
        </div>
        <div className="form-group fv-plugins-icon-container">
          <div className="password-container mt-8">
            <label
              className={`form-label mb-0 ${
                lang == "ar" ? "direction-rtl" : ""
              }`}
            >
              {t.auth.create_password_form.confirm_button}
            </label>
            <input
              placeholder={
                t.auth.create_password_form.confirm_password_placeholder
              }
              type={showConfirmPassword ? "text" : "password"}
              className={`form-control form-control-solid ${getInputClasses(
                "passwordConfirmation"
              )} email-password-ltr ${
                formik.touched.passwordConfirmation &&
                formik.errors.passwordConfirmation
                  ? "input-error"
                  : ""
              }`}
              name="passwordConfirmation"
              {...formik.getFieldProps("passwordConfirmation")}
            />
            <span
              className={`${
                lang == "ar" ? "password-toggle-rtl" : "password-toggle"
              }`}
            >
              {showConfirmPassword ? (
                <i
                  id="eyeIcon"
                  className="fa fa-eye"
                  aria-hidden="true"
                  onClick={() => setShowConfirmPassword(false)}
                ></i>
              ) : (
                <span onClick={() => setShowConfirmPassword(true)}>
                  <EyeIcon />
                </span>
              )}
            </span>
          </div>

          {formik.touched.passwordConfirmation &&
          formik.errors.passwordConfirmation ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block d-flex mt-2">
                <ErrorIcon />{" "}
                <p className="ml-1"> {formik.errors.passwordConfirmation}</p>{" "}
              </div>
            </div>
          ) : null}
        </div>

        <div className="">
          <button
            id="kt_login_signin_submit"
            type="submit"
            disabled={formik.isSubmitting}
            className={`common-text forget-password-btn justify-content-center mt-10 w-100 d-flex btn`}
          >
            <span>{t.auth.create_password_form.continue}</span>
            {loading && (
              <span
                className={`mt-4 spinner spinner-white  ${
                  lang == "ar" ? "mr-8" : "ml-3"
                }`}
              ></span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(ResetPassword));
